import React, { useRef, useCallback, useEffect } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';

export default function CardGrid({ 
  cards, 
  loading, 
  loadingMore,
  total, 
  hasMore, 
  onLoadMore, 
  onCardClick,
  lang,
  displaySize = 'normal'
}) {
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const { t } = useLang();

  // Intersection Observer para scroll infinito
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loading && !loadingMore) {
      onLoadMore();
    }
  }, [hasMore, loading, loadingMore, onLoadMore]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '200px',
      threshold: 0
    };
    observerRef.current = new IntersectionObserver(handleObserver, option);
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  // Estado de carga inicial
  if (loading && cards.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <>
      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[#A1A1AA] text-sm">
          {total > 0 
            ? `${total.toLocaleString()} ${t('catalog.cards_found')}`
            : t('catalog.no_cards_found')}
        </p>
      </div>

      {/* Cards Grid */}
      <div className={displaySize === 'small'
        ? "grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 mb-8"
        : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8"
      }>
        {cards.map((card, idx) => (
          <CardItem 
            key={`${card.external_id}-${idx}`}
            card={card}
            onClick={() => onCardClick(card)}
            lang={lang}
            small={displaySize === 'small'}
          />
        ))}
      </div>

      {/* Infinite scroll trigger */}
      <div ref={loadMoreRef} className="flex justify-center py-8">
        {loadingMore && (
          <div className="flex items-center gap-2 text-[#A1A1AA]">
            <Loader2 size={20} className="animate-spin text-[#10B981]" />
            <span className="text-sm">{t('catalog.loading_more')}</span>
          </div>
        )}
        {!hasMore && cards.length > 0 && (
          <p className="text-[#A1A1AA] text-sm">
            {t('catalog.no_more_cards')}
          </p>
        )}
      </div>
    </>
  );
}

function CardItem({ card, onClick, lang, small = false }) {
  return (
    <div
      onClick={onClick}
      className={`bg-[#12121A] border border-white/5 rounded-xl overflow-hidden cursor-pointer hover:border-white/20 hover:scale-[1.02] transition-all group ${small ? '' : ''}`}
      data-testid={`card-${card.external_id}`}
    >
      <div className={small ? "aspect-[2.5/3.5] relative overflow-hidden" : "aspect-[2.5/3.5] relative overflow-hidden"}>
        {card.image_url ? (
          <img 
            src={card.image_url} 
            alt={card.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a24] flex items-center justify-center">
            <Sparkles size={small ? 12 : 24} className="text-[#A1A1AA]/30" />
          </div>
        )}
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="absolute bottom-1 left-1 right-1">
            <p className={`text-white font-semibold truncate ${small ? 'text-[8px]' : 'text-xs'}`}>{card.name}</p>
          </div>
        </div>
      </div>
      {!small && (
        <div className="p-3">
          <p className="text-white text-sm font-semibold truncate">{card.name}</p>
          {card.set_name && (
            <p className="text-[#10B981] text-xs mt-0.5 truncate">{card.set_name}</p>
          )}
          {card.rarity && (
            <p className="text-[#C9A962] text-xs mt-0.5 truncate">{card.rarity}</p>
          )}
        </div>
      )}
    </div>
  );
}
